import React, { useState, useEffect } from 'react';
import Context, { useContainer } from './container';
import { isSSR } from './scripts/utils.js';
import Ceiling from './components/Ceiling/index.jsx';
import Footer from './components/Footer/index.jsx';
import Router from './router';
import ErrorBoundary from './components/ErrorBoundary';
import { defaultLanguage } from './components/constants';
import { getSites, getSiteInfoById } from './services';

// Vite 不需要 react-app-polyfill
import ScrollToTop from './components/ScrollToTop';
import 'dayjs/locale/zh-cn';
import dayjs from 'dayjs';
import useLazyImage from './scripts/hooks/useLazyImage';

export const STORAGE_LANG = 'TERMINUS_LANG';

interface IProps {
  staticContext: any;
}

interface languageType {
  id: number;
  title: string;
  desc: string;
  logo: string;
  mark: string;
  siteUrl: string;
  siteName: string;
  bottomNavigationList: any[];
  productList: any[];
  queryList: any[];
  solutionList: any[];
  successList: any[];
  topNavigationList: any[];
}

async function getSiteInfo(id: string | undefined, sites: any[], host: String) {
  const labels = sites.map((site) => site.mark);
  const [mark] = host.split('.');

  // default language is 'cn'
  let lang = defaultLanguage;
  if (labels.includes(mark)) {
    lang = mark;
  }

  let [site] = sites?.filter((_site: any) => _site.mark === lang);
  if (!site) {
    site = sites[0];
  }

  if (site?.id) {
    const language: any = await getSiteInfoById(site.id);

    return { currentSite: site, language, lang };
  }
}
function App({ staticContext }: IProps) {
  // 安全地获取 host，避免 SSR 中的 hydration 问题
  const getHost = () => {
    if (isSSR) {
      return staticContext.host || 'localhost:3100';
    }
    return staticContext.host || window.location.host;
  };
  
  const host = getHost().replace('www.', '');
  const [sites, setSites]: [any[], Function] = useState(staticContext.sites || []);
  const [currentSite, setCurrentSite] = useState(staticContext.currentSite || {});
  const [language, setLanguage]: [languageType | undefined, Function] = useState(staticContext.language);
  const [lang, setLang]: [string | undefined, Function] = useState(staticContext.lang);
  const { state, dispatch } = useContainer();
  const { updateObserve } = useLazyImage({ offset: 100 });

  useEffect(() => {
    if (isSSR) {
      return
    }
    if (!currentSite.id) {
      fetchSite();
    }
  }, [isSSR, currentSite.id]);

  useEffect(() => {
    if (isSSR) {
      return
    }
    if (currentSite.id) {
      localStorage.setItem('TERMINUS_LANG_ID', currentSite?.id ?? '1')
    }
  }, [isSSR, currentSite.id]);

  useEffect(() => {
    if (isSSR || !language) {
      return
    }
    document.body.setAttribute('class', (language.mark || '').toLowerCase());
  }, [language]);

  useEffect(() => {
    if (isSSR) {
      return
    }
    localStorage.setItem(STORAGE_LANG, lang ?? '');
  }, [lang]);

  async function fetchSite() {
    const result = await App.getInitialProps(host);
    setSites(result.sites || []);
    setCurrentSite(result.currentSite || {});
    setLanguage(result.language);
    setLang(result.lang);
    result.lang === 'cn' ? dayjs.locale('zh-cn') : dayjs.locale('en');
  }

  return (
    <ErrorBoundary>
      <Context.Provider
        value={{
          ...state,
          dispatch,
          sites,
          currentSite,
          language,
          host,
          lang,
          updateObserve,
          theme: state.theme || undefined // 确保 theme 有默认值
        }}
      >
        <Ceiling />
        <Router initialData={staticContext.initialData} route={staticContext.currentRoute} />
        <Footer />
        <ScrollToTop />
      </Context.Provider>
    </ErrorBoundary>
  );
}

App.getInitialProps = async (host: String) => {
  const sites: any = await getSites();
  const result = await getSiteInfo(undefined, sites, host);
  return { sites, ...result };
};

export default App;
